import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../axios/axiosInstance';
import apiEndpoints from '../../../axios/api';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import { usePageRefresh } from '../../../context/PageRefreshContext';

// Committee membership. A committee role is given to a member ALONGSIDE their
// Resident role, so after signing in again they can switch between the two
// from the society switcher in the header. Society Admin and Chairman
// (society.manage_users) can add and remove; other committee roles only view.

const inputClass = 'tw-w-full tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100';

const CommitteeMembers = ({ societyId }) => {
    const { can } = usePermissions();
    const canManage = can(PERMISSIONS.SOCIETY_MANAGE_USERS);

    const [members, setMembers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [adding, setAdding] = useState(false);
    const [search, setSearch] = useState('');
    const [candidates, setCandidates] = useState([]);
    const [userId, setUserId] = useState('');
    const [roleId, setRoleId] = useState('');
    const [saving, setSaving] = useState(false);
    const [removeTarget, setRemoveTarget] = useState(null);

    const load = useCallback(async () => {
        if (!societyId) return;
        setLoading(true);
        setError('');
        try {
            const [m, r] = await Promise.all([
                axiosInstance.get(apiEndpoints.COMMITTEE_MEMBERS_API, { params: { society_id: societyId } }),
                axiosInstance.get(apiEndpoints.COMMITTEE_ROLES_API, { params: { society_id: societyId } }),
            ]);
            setMembers(Array.isArray(m?.data) ? m.data : []);
            setRoles(Array.isArray(r?.data) ? r.data : []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load committee');
        } finally {
            setLoading(false);
        }
    }, [societyId]);

    useEffect(() => { load(); }, [load]);
    usePageRefresh(load);

    // Member search for the Add dialog (debounced).
    useEffect(() => {
        if (!adding) return undefined;
        const t = setTimeout(() => {
            axiosInstance.get(apiEndpoints.COMMITTEE_ELIGIBLE_USERS_API, { params: { society_id: societyId, search } })
                .then(res => setCandidates(Array.isArray(res?.data) ? res.data : []))
                .catch(() => setCandidates([]));
        }, 250);
        return () => clearTimeout(t);
    }, [adding, search, societyId]);

    const openAdd = () => {
        setAdding(true);
        setSearch('');
        setUserId('');
        setRoleId('');
    };

    const handleAdd = async () => {
        if (!userId || !roleId) {
            toast.error('Select a member and a committee role');
            return;
        }
        setSaving(true);
        try {
            const res = await axiosInstance.post(apiEndpoints.COMMITTEE_MEMBERS_API, {
                society_id: societyId, user_id: Number(userId), role_id: Number(roleId),
            });
            toast.success(res?.message || 'Added to the committee');
            setAdding(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Could not add');
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        if (!removeTarget) return;
        setSaving(true);
        try {
            await axiosInstance.delete(apiEndpoints.COMMITTEE_MEMBER_DELETE_API.replace(':id', removeTarget.user_society_id), {
                params: { society_id: societyId },
            });
            toast.success('Committee role removed');
            setRemoveTarget(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Could not remove');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-p-4 tw-border-b tw-border-gray-100">
                <div>
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800">Committee Members</h2>
                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                        Members keep their Resident role and can switch to their committee role from the society switcher (top right).
                    </p>
                </div>
                {canManage && (
                    <button
                        onClick={openAdd}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700"
                    >
                        <Icon icon="mdi:account-plus-outline" className="tw-text-base" />
                        Add Committee Member
                    </button>
                )}
            </div>

            {loading ? (
                <div className="tw-flex tw-justify-center tw-py-12">
                    <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
                </div>
            ) : error ? (
                <p className="tw-m-4 tw-text-sm tw-text-rose-700 tw-bg-rose-50 tw-rounded-lg tw-p-3">{error}</p>
            ) : members.length === 0 ? (
                <div className="tw-text-center tw-py-12">
                    <Icon icon="mdi:account-group-outline" className="tw-text-4xl tw-text-gray-300 tw-mx-auto" />
                    <p className="tw-text-sm tw-text-gray-500 tw-mt-2">No committee members yet.</p>
                </div>
            ) : (
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                {['Member', 'Committee role', 'Flat', 'Contact', 'Role switch', ...(canManage ? [''] : [])].map(h => (
                                    <th key={h || 'actions'} className="tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {members.map(m => (
                                <tr key={m.user_society_id} className="tw-border-b tw-border-gray-50">
                                    <td className="tw-px-3 tw-py-2.5 tw-font-medium tw-text-gray-800">{m.name}</td>
                                    <td className="tw-px-3 tw-py-2.5">
                                        <span className="tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold tw-bg-violet-100 tw-text-violet-700">{m.role_name}</span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2.5 tw-text-gray-600">{m.flat_label || '—'}</td>
                                    <td className="tw-px-3 tw-py-2.5 tw-text-gray-600">
                                        <div>{m.phone || '—'}</div>
                                        <div className="tw-text-[10px] tw-text-gray-400">{m.email}</div>
                                    </td>
                                    <td className="tw-px-3 tw-py-2.5">
                                        {m.can_switch
                                            ? <span className="tw-text-emerald-700">Resident ⇄ {m.role_name}</span>
                                            : <span className="tw-text-gray-400" title="Committee is this member's only role">Committee only</span>}
                                    </td>
                                    {canManage && (
                                        <td className="tw-px-3 tw-py-2.5 tw-text-right">
                                            <button
                                                onClick={() => setRemoveTarget(m)}
                                                className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600"
                                                title="Remove committee role"
                                            >
                                                <Icon icon="mdi:account-remove-outline" className="tw-text-base" />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add dialog */}
            {adding && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4" onClick={() => setAdding(false)}>
                    <div className="tw-bg-white tw-rounded-xl tw-w-full tw-max-w-md tw-max-h-[calc(100vh-2rem)] tw-overflow-y-auto tw-shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Add Committee Member</h3>
                            <button onClick={() => setAdding(false)} className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100">
                                <Icon icon="mdi:close" className="tw-text-base tw-text-gray-400" />
                            </button>
                        </div>
                        <div className="tw-p-5 tw-space-y-4">
                            <div>
                                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">Member <span className="tw-text-rose-500">*</span></label>
                                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email or phone" className={`${inputClass} tw-mb-2`} />
                                <select value={userId} onChange={e => setUserId(e.target.value)} className={`${inputClass} tw-bg-white`}>
                                    <option value="">Select a member</option>
                                    {candidates.map(c => (
                                        <option key={c.user_id} value={c.user_id}>
                                            {c.name}{c.flat_label ? ` — ${c.flat_label}` : ''}{c.phone ? ` · ${c.phone}` : ''}
                                        </option>
                                    ))}
                                </select>
                                {candidates.length === 0 && (
                                    <p className="tw-text-[11px] tw-text-gray-400 tw-mt-1">No members found. Only owners, tenants and family members with a login can be added.</p>
                                )}
                            </div>
                            <div>
                                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">Committee role <span className="tw-text-rose-500">*</span></label>
                                <select value={roleId} onChange={e => setRoleId(e.target.value)} className={inputClass}>
                                    <option value="">Select role</option>
                                    {roles.map(r => <option key={r.role_id} value={r.role_id}>{r.role_name}</option>)}
                                </select>
                            </div>
                            <p className="tw-text-[11px] tw-text-gray-500">
                                The member keeps their Resident role. After signing in again they pick the committee role
                                from the society switcher to manage notices, complaints and tasks.
                            </p>
                        </div>
                        <div className="tw-flex tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100">
                            <button onClick={() => setAdding(false)} className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">Cancel</button>
                            <button onClick={handleAdd} disabled={saving} className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-md hover:tw-bg-blue-700 disabled:tw-opacity-50">
                                {saving ? 'Adding…' : 'Add'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove confirmation */}
            {removeTarget && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4" onClick={() => setRemoveTarget(null)}>
                    <div className="tw-bg-white tw-rounded-xl tw-w-full tw-max-w-sm tw-shadow-xl tw-p-5" onClick={e => e.stopPropagation()}>
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Remove committee role?</h3>
                        <p className="tw-text-xs tw-text-gray-600 tw-mt-2">
                            {removeTarget.name} will no longer be {removeTarget.role_name}. Their Resident access is not affected.
                        </p>
                        <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
                            <button onClick={() => setRemoveTarget(null)} className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">Cancel</button>
                            <button onClick={handleRemove} disabled={saving} className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-bg-rose-600 tw-rounded-md hover:tw-bg-rose-700 disabled:tw-opacity-50">
                                {saving ? 'Removing…' : 'Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CommitteeMembers;
